import Form from 'antd/lib/form';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Input from 'antd/lib/input';
import Checkbox from 'antd/lib/checkbox';
import { useDispatch, useSelector } from 'react-redux';
import { useWatch } from 'antd/es/form/Form';
import { AppComponentProps } from '@/typescriptHelpers';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { UpdateFixesFeatureSettingsRequest, tryUpdateAppSettings } from '@/actions/appSettings';
import {
    selectNotificationIntegration,
    selectPreventFixRequestsAfterSubmission,
    selectPreventFixRequestsAfterSubmissionCustomCopy,
    selectPreventFixRequestsAfterSubmissionCustomCopyEnabled,
    selectThreadFixNotifications,
} from '@/selectors/selectors';
import { RootState } from 'reducers/reducers';
import { IntegrationType } from '@/types';
import { antGrey } from '../styles/globalStyles';
import { useFixSettingsModal } from './useFixSettingsModal';
import FixSettingsDrawer from './FixSettingsDrawer';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

interface EditGeneralFixSettingsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditGeneralFixSettingsModal: React.FC<EditGeneralFixSettingsModalProps> = (
    props: EditGeneralFixSettingsModalProps,
) => {
    const { form, isLoading, afterClose } = useFixSettingsModal({ open: props.open, onCancel: props.onCancel });
    const dispatch = useDispatch();

    const threadFixNotifications = useSelector(selectThreadFixNotifications(props.appId));
    const preventFixRequestsAfterSubmission = useSelector(selectPreventFixRequestsAfterSubmission(props.appId));
    const preventFixRequestsAfterSubmissionCustomCopy = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopy(props.appId),
    );
    const preventFixRequestsAfterSubmissionCustomCopyEnabled = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopyEnabled(props.appId),
    );
    const notificationIntegration = useSelector((state: RootState) =>
        selectNotificationIntegration(state, props.appId),
    );

    // threading is only supported in Slack
    const hasSlackIntegration =
        notificationIntegration != null ? notificationIntegration.id === IntegrationType.Slack : false;

    const isPreventFixRequestsAfterSubmissionEnabled = useWatch(['preventFixRequestsAfterSubmission'], form);
    const isPreventFixRequestsAfterSubmissionCustomCopyEnabled = useWatch(
        ['preventFixRequestsAfterSubmissionCustomCopyEnabled'],
        form,
    );

    const initialValues = {
        preventFixRequestsAfterSubmission: preventFixRequestsAfterSubmission,
        preventFixRequestsAfterSubmissionCustomCopy: preventFixRequestsAfterSubmissionCustomCopy,
        preventFixRequestsAfterSubmissionCustomCopyEnabled: preventFixRequestsAfterSubmissionCustomCopyEnabled,
        threadFixNotifications: threadFixNotifications,
    };

    const onFinish = (value) => {
        const request: UpdateFixesFeatureSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            preventFixRequestsAfterSubmission: value.preventFixRequestsAfterSubmission,
            preventFixRequestsAfterSubmissionCustomCopyEnabled:
                value.preventFixRequestsAfterSubmissionCustomCopyEnabled,
            preventFixRequestsAfterSubmissionCustomCopy: value.preventFixRequestsAfterSubmissionCustomCopy,
            // Only Slack supports threading, so the field is omitted (left untouched) otherwise.
            ...(hasSlackIntegration ? { threadFixNotifications: value.threadFixNotifications } : {}),
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RequiresFixRequestsApprovals, request));
    };

    return (
        <FixSettingsDrawer
            title="General"
            open={props.open}
            loading={isLoading}
            onSave={form.submit}
            onCancel={props.onCancel}
            afterClose={afterClose}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text style={{ color: antGrey.grey7 }}>Configure general behavior for fixes.</Text>
                <Form
                    onFinish={onFinish}
                    initialValues={initialValues}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="preventFixRequestsAfterSubmission"
                        valuePropName="checked"
                        style={{ marginBottom: 4 }}
                    >
                        <Checkbox>Disallow new fix requests once release is submitted</Checkbox>
                    </Form.Item>
                    {isPreventFixRequestsAfterSubmissionEnabled ?
                        <Form.Item
                            name="preventFixRequestsAfterSubmissionCustomCopyEnabled"
                            valuePropName="checked"
                            style={{ marginBottom: 4, marginLeft: 12 }}
                        >
                            <Checkbox>Show a message alongside disallowed action</Checkbox>
                        </Form.Item>
                    :   null}
                    {(
                        isPreventFixRequestsAfterSubmissionCustomCopyEnabled &&
                        isPreventFixRequestsAfterSubmissionEnabled
                    ) ?
                        <Form.Item
                            name="preventFixRequestsAfterSubmissionCustomCopy"
                            style={{ marginLeft: 12 }}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (
                                            isPreventFixRequestsAfterSubmissionCustomCopyEnabled &&
                                            isPreventFixRequestsAfterSubmissionEnabled &&
                                            !value
                                        ) {
                                            return Promise.reject(new Error('Custom copy is required'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input.TextArea rows={5} />
                        </Form.Item>
                    :   null}
                    {hasSlackIntegration && (
                        <Form.Item name="threadFixNotifications" valuePropName="checked" style={{ marginBottom: 4 }}>
                            <Checkbox>
                                Thread related fix notifications
                                <TooltipInfoIcon
                                    tooltipContent="Notifications relevant to a given fix will be threaded in Slack"
                                    style={{ marginLeft: 8 }}
                                    onClick={(e) => e.preventDefault()}
                                />
                            </Checkbox>
                        </Form.Item>
                    )}
                </Form>
            </div>
        </FixSettingsDrawer>
    );
};

export default EditGeneralFixSettingsModal;
